import { createDefaultSectionConfig } from './reportPreviewUtils';

export function getSectionColumnKeys(section) {
  return (section?.columns || []).map((col) => col.key);
}

export function pageColumnsToSectionConfig(page, section) {
  const allKeys = getSectionColumnKeys(section);
  const defaultTitle = section?.title || section?.name || '';
  const base = {
    ...createDefaultSectionConfig(section),
    titleOverride: page?.label && page.label !== defaultTitle ? page.label : '',
    showTitle: page?.showTitle !== false
  };

  if (!Array.isArray(page?.columns) || page.columns.length === 0) {
    return base;
  }

  const visibleSet = new Set(page.columns);
  const hiddenKeys = allKeys.filter((key) => !visibleSet.has(key));
  const columnOrder = [
    ...page.columns.filter((key) => allKeys.includes(key)),
    ...allKeys.filter((key) => !visibleSet.has(key))
  ];

  return {
    ...base,
    columnOrder,
    hiddenKeys
  };
}

export function sectionConfigToPageColumns(config, section) {
  const allKeys = getSectionColumnKeys(section);
  const order = config?.columnOrder?.length ? config.columnOrder : allKeys;
  const hidden = new Set(config?.hiddenKeys || []);
  return order.filter((key) => allKeys.includes(key) && !hidden.has(key));
}

export function sectionConfigToPageUpdates(config, section) {
  const allKeys = getSectionColumnKeys(section);
  const columns = sectionConfigToPageColumns(config, section);
  const defaultTitle = section?.title || section?.name || '';
  const titleOverride = (config?.titleOverride || '').trim();

  const isFullDefault = columns.length === allKeys.length
    && columns.every((key, index) => key === allKeys[index]);

  return {
    columns: isFullDefault ? undefined : columns,
    label: titleOverride && titleOverride !== defaultTitle ? titleOverride : undefined,
    showTitle: config?.showTitle === false ? false : undefined
  };
}

export function buildSectionConfigsFromPages(pages, reportData) {
  const configs = {};
  const pageByInstance = Object.fromEntries(
    (pages || []).map((page) => [page.instanceId || page.sectionKey, page])
  );

  (reportData?.sections || []).forEach((section) => {
    const key = section.instanceId || section.sectionKey;
    const page = pageByInstance[key];
    configs[key] = page
      ? pageColumnsToSectionConfig(page, section)
      : createDefaultSectionConfig(section);
  });

  return configs;
}

export function mergeSectionConfigsIntoPages(pages, sectionConfigs, sectionMap) {
  return pages.map((page) => {
    const meta = sectionMap?.[page.sectionKey];
    if (!meta || meta.sectionType !== 'data-table') return page;

    const key = page.instanceId || page.sectionKey;
    const config = sectionConfigs?.[key];
    if (!config) return page;

    const updates = sectionConfigToPageUpdates(config, { ...meta, title: meta.name });
    const next = { ...page };

    if (updates.columns?.length) {
      next.columns = updates.columns;
    } else {
      delete next.columns;
    }

    if (updates.label) {
      next.label = updates.label;
    } else if (config.titleOverride === '') {
      delete next.label;
    }

    if (updates.showTitle === false) {
      next.showTitle = false;
    } else {
      delete next.showTitle;
    }

    return next;
  });
}

export function formatColumnSelectionSummary(page, section) {
  const total = getSectionColumnKeys(section).length;
  if (!total) return '';
  const selected = Array.isArray(page?.columns) && page.columns.length
    ? page.columns.length
    : total;
  if (selected === total) return `${total} columns`;
  return `${selected} of ${total} columns`;
}
